import axios from 'axios';
import { db } from '../firebase';
import { getDoc, doc } from 'firebase/firestore';
import { League } from '../context/LeagueContext';

const API_BASE = process.env.REACT_APP_API_URL;

export type ClutchTeam = {
  id: string;
  homeTeam: string;
  awayTeam: string;
  homeScore: number;
  awayScore: number;
  status: string;
  time: string;
};

export type ClutchBreakdown = {
  teamId?: string;
  score: number;
  breakdown: {
    last10?: number;
    winStreak?: number;
    runDiff?: number;
    injuries?: number;
    hypeNews?: number;
    antiHype?: number;
    overUnderBonus?: number;
    underdogBoost?: number;
    eliteBonus?: number;
    homeFieldBonus?: number;
    fatiguePenalty?: number;
    dominanceBonus?: number;
    balancedTeamBonus?: number;
    roadWarriorBonus?: number;
    divisionFavoriteBonus?: number;
    wildcardChaserBonus?: number;
    playoffPenalty?: number;
    playoffStatus?: 'active' | 'eliminated';
    hypeHits: { headline: string; url: string; source: string }[];
    antiHypeHits: { headline: string; url: string; source: string }[];
    neutralHits: { headline: string; url: string; source: string }[];
  };
};


// 🧠 Try cached clutch score first, then ping BE quietly
export async function fetchClutchScore(
  league: League,
  teamId: string
): Promise<ClutchBreakdown> {
  try {
    const cache = await fetchClutchFromFirestore(league, teamId);
    if (cache) {
      console.log(`✅ Loaded clutch score from Firestore: ${league}/${teamId}`);
      return cache;
    }
  } catch (err) {
    const error = err as Error;
    console.warn(`⚠️ Failed to load from Firestore: ${error.message}`);
  }

  // Backend fallback
  console.warn(`⚡ Firestore miss, pinging backend for: ${league}/${teamId}`);
  await pingBackendWakeup(league, teamId); // try to wake server if cold

  const res = await axios.get(`${API_BASE}/hype/${league}/${teamId}`);
  return res.data;
}

export async function fetchAllClutchScores(league: League): Promise<{ updatedAt: number; results: any[] }> {
  const res = await axios.get(`${API_BASE}/hype/${league}`);
  return res.data; // JUST RETURN RAW
}


// 🧹 Fetch clutch score directly from Firestore
export async function fetchClutchFromFirestore(league: League, teamId: string) {
  const clutchDoc = doc(db, 'hype', `hype_${league}_${teamId}`);
  const snap = await getDoc(clutchDoc);

  if (snap.exists()) {
    return snap.data()?.data;
  }
  return null;
}

// 🛠 Silent backend wakeup ping (non-blocking)
async function pingBackendWakeup(league: League, teamId: string) {
  try {
    await axios.get(`${API_BASE}/hype/${league}/${teamId}`, { timeout: 1000 });
  } catch (err) {
    const error = err as Error;
    console.warn(`Backend still waking for ${teamId}`, error.message);
  }
}

// --- No need to change fetchParsedGames() yet ---
export async function fetchParsedGames(league: League): Promise<ClutchTeam[]> {
  const res = await axios.get(`${API_BASE}/scores/${league}`);
  const events = res.data?.events || [];

  return events.map((event: any) => {
    const home = event.competitions[0].competitors.find((c: any) => c.homeAway === 'home');
    const away = event.competitions[0].competitors.find((c: any) => c.homeAway === 'away');

    return {
      id: event.id,
      homeTeam: home.team.displayName,
      awayTeam: away.team.displayName,
      homeScore: Number(home.score),
      awayScore: Number(away.score),
      status: event.status.type.name,
      time: event.status.displayClock || event.status.type.shortDetail,
    };
  });
}

export async function fetchLeaderboard(league: League) {
  try {
    const cacheDocRef = doc(db, 'meta', `hype_leaderboard_${league}`);
    const cacheDocSnap = await getDoc(cacheDocRef);

    if (cacheDocSnap.exists()) {
      const data = cacheDocSnap.data();
      const updatedAt = data.updatedAt || 0;
      const now = Date.now();
      const eightHours = 8 * 60 * 60 * 1000;

      if (now - updatedAt < eightHours) {
        console.log(`✅ Loaded fresh leaderboard cache for ${league}`);
        return data;
      }

      console.warn(`⏳ Cache stale for ${league}, refetching...`);
    }
  } catch (err) {
    console.warn(`⚠️ Failed to load leaderboard cache:`, (err as Error).message);
  }

  console.warn(`⚡ Falling back to backend for leaderboard: ${league}`);
  const res = await axios.get(`${process.env.REACT_APP_API_URL}/leaderboard/${league}`);
  return res.data;
}

